import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const whyListSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is your management fee?',
      acceptedAnswer: { '@type': 'Answer', text: 'Our standard management fee is 25% of gross rental revenue. This covers all services including marketing, guest communication, cleaning coordination, maintenance management, and 24/7 support.' },
    },
    {
      '@type': 'Question',
      name: 'Can I use my property for personal use?',
      acceptedAnswer: { '@type': 'Answer', text: 'Absolutely! You can block off dates for personal use through the owner portal. We recommend coordinating your personal stays to maximize rental income during peak seasons.' },
    },
    {
      '@type': 'Question',
      name: 'What types of properties do you accept?',
      acceptedAnswer: { '@type': 'Answer', text: 'We focus on high-quality waterfront properties in Port Severn and surrounding areas. Properties must meet our standards for safety, amenities, and guest experience.' },
    },
    {
      '@type': 'Question',
      name: 'How do you handle maintenance issues?',
      acceptedAnswer: { '@type': 'Answer', text: 'We have a network of trusted local contractors and handle all maintenance coordination. Routine maintenance is included, and we\'ll consult with you on any major repairs or improvements.' },
    },
    {
      '@type': 'Question',
      name: 'When do I receive payments?',
      acceptedAnswer: { '@type': 'Answer', text: 'Owner payments are processed monthly, typically within 15 days after month-end. You\'ll receive detailed statements through the owner portal.' },
    },
  ],
};

export default function WhyListPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [propertyValue, setPropertyValue] = useState(500000);
  const [nightlyRate, setNightlyRate] = useState(400);
  const [occupancyRate, setOccupancyRate] = useState(65);

  const calculateRevenue = () => {
    const nightsPerYear = 365 * (occupancyRate / 100);
    const grossRevenue = nightsPerYear * nightlyRate;
    const managementFee = grossRevenue * 0.25;
    const netRevenue = grossRevenue - managementFee;
    return { gross: Math.round(grossRevenue), net: Math.round(netRevenue), roi: ((netRevenue / propertyValue) * 100).toFixed(2) };
  };

  const revenue = calculateRevenue();

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Why List With True North Vacations | Maximize Waterfront Rental Income"
        description="Discover why Port Severn property owners choose True North Vacations. Revenue calculator, 25% management fee, zero effort, professional maintenance, and transparent reporting. Maximize your waterfront rental income today."
        keywords="list waterfront property Ontario, vacation rental management benefits, cottage rental income Port Severn, property owner revenue calculator, Airbnb management Ontario"
        canonical={`${import.meta.env.VITE_SITE_URL || ''}/why-list`}
        ogTitle="Why List With True North Vacations | Maximize Waterfront Rental Income"
        ogDescription="Discover why Port Severn property owners choose True North Vacations. Revenue calculator, zero effort, professional maintenance."
        schemaJson={whyListSchema}
      />
      {/* Navigation */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-compass-3-line text-2xl text-[#0A2540]"></i>
              <span className="text-base sm:text-xl font-serif font-semibold text-[#0A2540]">True North Vacations</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link to="/listings" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Properties</Link>
              <Link to="/why-list" className="text-sm font-light text-[#C9A961] whitespace-nowrap">For Owners</Link>
              <Link to="/services" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Services</Link>
              <Link to="/owners-portal" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Owner Portal</Link>
              <Link to="/contact" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/contact" className="bg-[#C9A961] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors whitespace-nowrap">List Your Property</Link>
            </div>
            <button className="lg:hidden p-2 text-[#0A2540] cursor-pointer" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
          {menuOpen && (
            <div className="lg:hidden pt-4 pb-4 border-t border-gray-100 mt-4 space-y-3">
              <Link to="/listings" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Properties</Link>
              <Link to="/why-list" onClick={() => setMenuOpen(false)} className="block text-sm text-[#C9A961] py-2">For Owners</Link>
              <Link to="/services" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Services</Link>
              <Link to="/owners-portal" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Owner Portal</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Contact</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="inline-block bg-[#C9A961] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors mt-2">List Your Property</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[280px] sm:h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/1569c29aeb6769c4d0a3ab72270d6110.jpg" alt="Why List With Us" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-5xl font-serif text-white mb-3 sm:mb-4">Why List With True North</h1>
          <p className="text-base sm:text-xl text-white/90 font-light">Maximize returns while preserving your property's value</p>
        </div>
      </section>

      {/* Revenue Calculator */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Revenue Calculator</h2>
            <p className="text-gray-600 text-base sm:text-lg">Estimate your potential rental income</p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
              <div className="space-y-6 sm:space-y-8">
                {[
                  { label: `Property Value: $${propertyValue.toLocaleString()}`, min: 200000, max: 2000000, step: 50000, value: propertyValue, setter: setPropertyValue },
                  { label: `Nightly Rate: $${nightlyRate}`, min: 200, max: 1000, step: 25, value: nightlyRate, setter: setNightlyRate },
                  { label: `Occupancy Rate: ${occupancyRate}%`, min: 30, max: 90, step: 5, value: occupancyRate, setter: setOccupancyRate }
                ].map((slider, i) => (
                  <div key={i}>
                    <label className="block text-sm font-semibold text-[#0A2540] mb-3">{slider.label}</label>
                    <input type="range" min={slider.min} max={slider.max} step={slider.step} value={slider.value} onChange={(e) => slider.setter(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#C9A961]" />
                  </div>
                ))}
              </div>
              <div className="bg-[#0A2540] rounded-2xl p-6 sm:p-8 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8">Your Estimated Annual Revenue</h3>
                <div className="space-y-5">
                  <div className="border-b border-white/20 pb-4">
                    <p className="text-gray-300 text-sm mb-1">Gross Revenue</p>
                    <p className="text-2xl sm:text-3xl font-bold text-[#C9A961]">${revenue.gross.toLocaleString()}</p>
                  </div>
                  <div className="border-b border-white/20 pb-4">
                    <p className="text-gray-300 text-sm mb-1">Management Fee (25%)</p>
                    <p className="text-lg sm:text-xl">-${(revenue.gross - revenue.net).toLocaleString()}</p>
                  </div>
                  <div className="border-b border-white/20 pb-4">
                    <p className="text-gray-300 text-sm mb-1">Net Revenue</p>
                    <p className="text-3xl sm:text-4xl font-bold">${revenue.net.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm mb-1">Return on Investment</p>
                    <p className="text-2xl sm:text-3xl font-bold text-[#C9A961]">{revenue.roi}%</p>
                  </div>
                </div>
                <div className="mt-6 bg-white/10 rounded-xl p-4">
                  <p className="text-xs text-gray-300">* Estimates based on typical Port Severn waterfront properties. Actual results may vary.</p>
                </div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <Link to="/contact" className="inline-flex items-center space-x-2 bg-[#C9A961] text-white px-8 py-4 rounded-full text-base sm:text-lg hover:bg-[#B8984E] transition-all whitespace-nowrap">
                <span>Get a Personalized Quote</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">The True North Advantage</h2>
            <p className="text-gray-600 text-base sm:text-lg">Why property owners choose us</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: 'ri-line-chart-line', title: 'Maximum Revenue', description: 'Dynamic pricing strategies and multi-platform exposure ensure you earn top dollar for every booking.' },
              { icon: 'ri-shield-check-line', title: 'Property Protection', description: 'Comprehensive insurance, guest screening, and regular inspections protect your investment.' },
              { icon: 'ri-tools-line', title: 'Professional Maintenance', description: 'Year-round exterior and interior care keeps your property in pristine condition.' },
              { icon: 'ri-time-line', title: 'Zero Time Commitment', description: 'We handle everything from guest communication to cleaning, so you can relax.' },
              { icon: 'ri-star-line', title: '5-Star Guest Experience', description: 'Premium service standards lead to excellent reviews and repeat bookings.' },
              { icon: 'ri-bar-chart-box-line', title: 'Transparent Reporting', description: 'Real-time access to bookings, revenue, and property performance through our portal.' }
            ].map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-[#C9A961]/10 rounded-full flex items-center justify-center mb-5">
                  <i className={`${benefit.icon} text-3xl text-[#C9A961]`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-[#0A2540] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">How It Works</h2>
            <p className="text-gray-600 text-base sm:text-lg">Getting started is simple</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { step: '01', title: 'Initial Consultation', description: 'Schedule a free consultation to discuss your property and goals.' },
              { step: '02', title: 'Property Evaluation', description: 'Our team assesses your property and creates a custom management plan.' },
              { step: '03', title: 'Professional Setup', description: 'We photograph, list, and optimize your property across all platforms.' },
              { step: '04', title: 'Start Earning', description: 'Sit back and watch the bookings roll in while we handle everything.' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8">
                <div className="text-5xl font-bold text-[#C9A961]/20 mb-4">{item.step}</div>
                <h3 className="text-lg sm:text-xl font-bold text-[#0A2540] mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 sm:mt-12">
            <Link to="/contact" className="inline-flex items-center space-x-2 bg-[#0A2540] text-white px-8 py-4 rounded-full text-base sm:text-lg hover:bg-[#0A2540]/90 transition-all whitespace-nowrap">
              <span>Schedule Your Consultation</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-5">
            {[
              { question: 'What is your management fee?', answer: 'Our standard management fee is 25% of gross rental revenue. This covers all services including marketing, guest communication, cleaning coordination, maintenance management, and 24/7 support.' },
              { question: 'Can I use my property for personal use?', answer: 'Absolutely! You can block off dates for personal use through the owner portal. We recommend coordinating your personal stays to maximize rental income during peak seasons.' },
              { question: 'What types of properties do you accept?', answer: 'We focus on high-quality waterfront properties in Port Severn and surrounding areas. Properties must meet our standards for safety, amenities, and guest experience.' },
              { question: 'How do you handle maintenance issues?', answer: 'We have a network of trusted local contractors and handle all maintenance coordination. Routine maintenance is included, and we\'ll consult with you on any major repairs or improvements.' },
              { question: 'When do I receive payments?', answer: 'Owner payments are processed monthly, typically within 15 days after month-end. You\'ll receive detailed statements through the owner portal.' }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-[#0A2540] mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-base sm:text-xl text-gray-300 mb-8 leading-relaxed">Join the growing community of property owners who trust True North Vacations to maximize their rental income and protect their investment.</p>
          <Link to="/contact" className="inline-flex items-center space-x-2 bg-[#C9A961] text-white px-8 py-4 rounded-full text-lg hover:bg-[#B8984E] transition-all whitespace-nowrap">
            <span>List Your Property Today</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-compass-3-line text-2xl text-[#C9A961]"></i>
                <span className="text-lg font-serif font-semibold">True North Vacations</span>
              </div>
              <p className="text-gray-400 text-sm">Port Severn's Premier Luxury Vacation Rental Management</p>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services" className="hover:text-white transition-colors">Property Management</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Maintenance</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Guest Services</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">For Owners</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">List Your Property</Link></li>
                <li><Link to="/owners-portal" className="hover:text-white transition-colors">Owner Portal</Link></li>
                <li><Link to="/why-list" className="hover:text-white transition-colors">Revenue Calculator</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Properties</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/listings" className="hover:text-white transition-colors">Current Listings</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Booking Inquiry</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 True North Vacations. All rights reserved.</p>
            <div className="flex items-center space-x-5">
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-facebook-fill text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-instagram-line text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-linkedin-fill text-xl"></i></a>
              <a href="mailto:info@truenorthvacations.com" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-mail-line text-xl"></i></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
